import { Trans } from '@lingui/macro'
import { Currency } from '@uniswap/sdk-core'
import { AutoColumn } from 'components/Column'
import CurrencyLogo from 'components/Logo/CurrencyLogo'
import Modal from 'components/Modal'
import { RowBetween } from 'components/Row'
import { useCallback, useState } from 'react'
import { Check, Copy, Share2 } from 'react-feather'
import styled, { css, keyframes } from 'styled-components/macro'
import { CloseIcon, ThemedText } from 'theme'

const Wrapper = styled.div`
  background-color: ${({ theme }) => theme.backgroundSurface};
  border-radius: 6px;
  outline: 1px solid ${({ theme }) => theme.backgroundOutline};
  width: 100%;
  padding: 16px;
`

const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`

const ShareIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.accentAction} 0%,
    ${({ theme }) => theme.accentSuccess} 100%
  );
  margin: 8px auto;
  ${css`
    animation: ${pulse} 2s ease-in-out infinite;
  `}
`

const StyledShareIcon = styled(Share2)`
  width: 36px;
  height: 36px;
  color: ${({ theme }) => theme.white};
`

const TokensContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin: 8px 0;
`

const TokenItem = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`

const TokenLogoWrapper = styled.div`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const RouteArrow = styled.div`
  font-size: 20px;
  color: ${({ theme }) => theme.textSecondary};
  display: flex;
  align-items: center;
  gap: 4px;
`

const URLInputContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
`

const URLInput = styled.input`
  flex: 1;
  padding: 8px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  background-color: ${({ theme }) => theme.backgroundModule};
  color: ${({ theme }) => theme.textPrimary};
  font-size: 13px;
  font-family: inherit;
  outline: none;
  text-align: left;
  direction: ltr;

  &:focus {
    border-color: ${({ theme }) => theme.accentAction};
  }

  &::selection {
    background-color: transparent;
  }
`

const CopyButton = styled.button<{ copied?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 12px 16px;
  border-radius: 8px;
  border: none;
  background-color: ${({ theme, copied }) => (copied ? theme.accentSuccess : theme.accentAction)};
  color: ${({ theme }) => theme.white};
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    opacity: 0.8;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`

const CopyIcon = styled(Copy)`
  width: 16px;
  height: 16px;
`

const CheckIcon = styled(Check)`
  width: 16px;
  height: 16px;
`

interface ShareModalProps {
  isOpen: boolean
  onDismiss: () => void
  inputCurrency?: Currency | null
  outputCurrency?: Currency | null
}

export default function ShareModal({ isOpen, onDismiss, inputCurrency, outputCurrency }: ShareModalProps) {
  const [copied, setCopied] = useState(false)

  const shareUrl = useCallback(() => {
    if (!inputCurrency || !outputCurrency) return ''

    const inputId = inputCurrency.isNative ? 'PLS' : inputCurrency.isToken ? inputCurrency.address : ''
    const outputId = outputCurrency.isNative ? 'PLS' : outputCurrency.isToken ? outputCurrency.address : ''

    const baseUrl = window.location.origin
    return `${baseUrl}/#/swap?inputCurrency=${inputId}&outputCurrency=${outputId}`
  }, [inputCurrency, outputCurrency])

  const handleCopy = useCallback(async () => {
    const url = shareUrl()
    if (!url) return

    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      console.error('Failed to copy:', error)
    }
  }, [shareUrl])

  const handleInputClick = useCallback((e: React.MouseEvent<HTMLInputElement>) => {
    const input = e.currentTarget
    if (input) {
      input.select()
    }
  }, [])

  const handleInputFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    const input = e.currentTarget
    if (!input) return

    // Move cursor to the beginning to show the start of the URL
    input.setSelectionRange(0, 0)
  }, [])

  return (
    <Modal isOpen={isOpen} onDismiss={onDismiss} maxHeight={90}>
      <Wrapper>
        <AutoColumn gap="md">
          <RowBetween>
            <ThemedText.SubHeader>
              <Trans>Share</Trans>
            </ThemedText.SubHeader>
            <CloseIcon onClick={onDismiss} />
          </RowBetween>

          <ShareIconWrapper>
            <StyledShareIcon />
          </ShareIconWrapper>

          <AutoColumn gap="sm">
            <ThemedText.BodySmall color="textSecondary" textAlign="center">
              <Trans>share this route</Trans>
            </ThemedText.BodySmall>

            <TokensContainer>
              <TokenItem>
                <TokenLogoWrapper>
                  <CurrencyLogo currency={inputCurrency ?? undefined} size="22px" />
                </TokenLogoWrapper>
                <ThemedText.SubHeader>{inputCurrency?.symbol || '—'}</ThemedText.SubHeader>
              </TokenItem>

              <RouteArrow>→</RouteArrow>

              <TokenItem>
                <TokenLogoWrapper>
                  <CurrencyLogo currency={outputCurrency ?? undefined} size="22px" />
                </TokenLogoWrapper>
                <ThemedText.SubHeader>{outputCurrency?.symbol || '—'}</ThemedText.SubHeader>
              </TokenItem>
            </TokensContainer>

            <URLInputContainer>
              <URLInput
                type="text"
                value={shareUrl()}
                readOnly
                onClick={handleInputClick}
                onFocus={handleInputFocus}
                placeholder="Generating share link..."
              />
              <CopyButton onClick={handleCopy} copied={copied}>
                {copied ? (
                  <>
                    <CheckIcon />
                    <Trans>Copied!</Trans>
                  </>
                ) : (
                  <>
                    <CopyIcon />
                    <Trans>Copy URL</Trans>
                  </>
                )}
              </CopyButton>
            </URLInputContainer>

            <ThemedText.Caption color="textTertiary" textAlign="center" marginTop="8px">
              <Trans>Anyone with this link can view and use this swap route</Trans>
            </ThemedText.Caption>
          </AutoColumn>
        </AutoColumn>
      </Wrapper>
    </Modal>
  )
}
